import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Activity,
  Mail,
  MessageSquare,
  RefreshCw,
  ExternalLink,
  CheckCircle2,
  XCircle,
  AlertTriangle,
} from 'lucide-react';

export default function NotificationsHealth() {
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [loadingLogs, setLoadingLogs] = useState(true);

  const [smsPhone, setSmsPhone] = useState('');
  const [smsSending, setSmsSending] = useState(false);

  const [testEmail, setTestEmail] = useState('');
  const [emailSending, setEmailSending] = useState(false);

  const loadStatus = async () => {
    setLoadingStatus(true);
    try {
      const res = await api.get('/notifications/service-status');
      setStatus(res.data);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load service status');
    } finally {
      setLoadingStatus(false);
    }
  };

  const loadLogs = async () => {
    setLoadingLogs(true);
    try {
      const res = await api.get('/notifications/logs?limit=20');
      setLogs(res.data?.logs || []);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load notification logs');
    } finally {
      setLoadingLogs(false);
    }
  };

  useEffect(() => {
    loadStatus();
    loadLogs();
  }, []);

  const handleSendTestSms = async () => {
    if (!smsPhone.trim()) {
      toast.error('Enter a phone number');
      return;
    }
    setSmsSending(true);
    try {
      const res = await api.post('/notifications/test-sms', { phone: smsPhone.trim() });
      toast.success(res.data?.message || 'Test SMS dispatched');
      if (res.data?.note) toast.info(res.data.note, { duration: 8000 });
      loadLogs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send SMS');
    } finally {
      setSmsSending(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail.trim()) {
      toast.error('Enter an email address');
      return;
    }
    setEmailSending(true);
    try {
      const res = await api.post('/notifications/test-email', { email: testEmail.trim() });
      toast.success(res.data?.message || 'Test email dispatched');
      if (res.data?.note) toast.info(res.data.note, { duration: 8000 });
      loadLogs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send email');
    } finally {
      setEmailSending(false);
    }
  };

  const fmtTime = (iso) => {
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  const statusBadge = (statusText = '') => {
    const s = statusText.toLowerCase();
    if (s.startsWith('sent')) {
      return (
        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
          <CheckCircle2 className="w-3 h-3 mr-1" /> Sent
        </Badge>
      );
    }
    if (s.startsWith('failed')) {
      return (
        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
          <XCircle className="w-3 h-3 mr-1" /> Failed
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
        <AlertTriangle className="w-3 h-3 mr-1" /> {statusText || 'Unknown'}
      </Badge>
    );
  };

  const dlrBadge = (log) => {
    // Email rows have no DLR concept — show n/a placeholder.
    if (log.type !== 'sms') {
      return <span className="text-xs text-muted-foreground">—</span>;
    }
    const status = (log.dlr_status || '').toLowerCase();
    const errCode = log.dlr_err_code;
    if (!status) {
      // Vonage hasn't pushed a DLR yet. Could be either pending or webhook not configured.
      return (
        <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200">
          Pending
        </Badge>
      );
    }
    if (status === 'delivered') {
      return (
        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
          <CheckCircle2 className="w-3 h-3 mr-1" /> Delivered
        </Badge>
      );
    }
    if (status === 'rejected') {
      return (
        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200" title={errCode ? `Vonage error ${errCode}` : undefined}>
          <XCircle className="w-3 h-3 mr-1" /> Rejected{errCode ? ` (${errCode})` : ''}
        </Badge>
      );
    }
    if (status === 'failed' || status === 'expired' || status === 'undeliverable') {
      return (
        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200" title={errCode ? `Vonage error ${errCode}` : undefined}>
          <XCircle className="w-3 h-3 mr-1" /> {status.charAt(0).toUpperCase() + status.slice(1)}
          {errCode ? ` (${errCode})` : ''}
        </Badge>
      );
    }
    if (status === 'accepted' || status === 'buffered') {
      return (
        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
        <AlertTriangle className="w-3 h-3 mr-1" /> {status}
      </Badge>
    );
  };

  return (
    <div className="container mx-auto px-4 py-6 max-w-5xl" data-testid="notifications-health-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Activity className="w-6 h-6" />
            Notifications Health
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Check service status, send diagnostic SMS/email, and review the last 20 delivery attempts.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => { loadStatus(); loadLogs(); }}
          data-testid="refresh-health-btn"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Service Status */}
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              SMS Service
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingStatus ? (
              <Skeleton className="h-16 w-full" />
            ) : status ? (
              <div className="space-y-1 text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">Provider:</span>
                  <span className="font-medium">{status.sms?.provider}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">Sender:</span>
                  <span className="font-medium">{status.sms?.phone_number}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">Status:</span>
                  {status.sms?.enabled ? (
                    <Badge className="bg-green-600">Active</Badge>
                  ) : (
                    <Badge variant="destructive">Not Configured</Badge>
                  )}
                </div>
                {status.sms?.note && (
                  <p className="text-xs text-muted-foreground mt-2">{status.sms.note}</p>
                )}
                <a
                  href="https://dashboard.nexmo.com/messages/sms"
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1 mt-2"
                >
                  Open Vonage dashboard <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Email Service
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingStatus ? (
              <Skeleton className="h-16 w-full" />
            ) : status ? (
              <div className="space-y-1 text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">Provider:</span>
                  <span className="font-medium">{status.email?.provider}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">Status:</span>
                  {status.email?.enabled ? (
                    <Badge className="bg-green-600">Active</Badge>
                  ) : (
                    <Badge variant="destructive">Not Configured</Badge>
                  )}
                </div>
                {status.email?.note && (
                  <p className="text-xs text-muted-foreground mt-2">{status.email.note}</p>
                )}
                <a
                  href="https://resend.com/emails"
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1 mt-2"
                >
                  Open Resend dashboard <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>

      {/* Send Diagnostics */}
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Send Test SMS</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="test-phone">Phone (E.164, e.g. +19039326550)</Label>
              <Input
                id="test-phone"
                placeholder="+1..."
                value={smsPhone}
                onChange={(e) => setSmsPhone(e.target.value)}
                data-testid="test-sms-phone-input"
              />
            </div>
            <Button
              onClick={handleSendTestSms}
              disabled={smsSending}
              className="w-full"
              data-testid="send-test-sms-btn"
            >
              {smsSending ? 'Sending...' : 'Send Test SMS'}
            </Button>
            <p className="text-xs text-muted-foreground">
              Sends a diagnostic SMS via Vonage. The Vonage message_id is recorded in the log table below.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Send Test Email</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="test-email">Email address</Label>
              <Input
                id="test-email"
                type="email"
                placeholder="name@example.com"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                data-testid="test-email-input"
              />
            </div>
            <Button
              onClick={handleSendTestEmail}
              disabled={emailSending}
              className="w-full"
              data-testid="send-test-email-btn"
            >
              {emailSending ? 'Sending...' : 'Send Test Email'}
            </Button>
            <p className="text-xs text-muted-foreground">
              Sends a diagnostic email via Resend. The Resend email_id is recorded in the log table below.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Logs */}
      <Card data-testid="notification-logs-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Recent Delivery Attempts (last 20)</CardTitle>
        </CardHeader>
        <CardContent>
          {loadingLogs ? (
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm">
              No notification attempts logged yet. Send a test above to see entries here.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b">
                    <th className="py-2 pr-3">Time</th>
                    <th className="py-2 pr-3">Type</th>
                    <th className="py-2 pr-3">Recipient</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3">DLR</th>
                    <th className="py-2 pr-3">Message ID</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log, idx) => (
                    <tr
                      key={`${log.timestamp}-${idx}`}
                      className="border-b last:border-0 hover:bg-muted/30"
                      data-testid={`notification-log-row-${idx}`}
                    >
                      <td className="py-2 pr-3 whitespace-nowrap text-muted-foreground">
                        {fmtTime(log.timestamp)}
                      </td>
                      <td className="py-2 pr-3">
                        <Badge variant="outline" className="capitalize">
                          {log.type === 'sms' ? (
                            <MessageSquare className="w-3 h-3 mr-1" />
                          ) : (
                            <Mail className="w-3 h-3 mr-1" />
                          )}
                          {log.type}
                        </Badge>
                      </td>
                      <td className="py-2 pr-3 font-mono text-xs break-all">{log.recipient}</td>
                      <td className="py-2 pr-3">{statusBadge(log.status)}</td>
                      <td className="py-2 pr-3" data-testid={`dlr-cell-${idx}`}>{dlrBadge(log)}</td>
                      <td className="py-2 pr-3 font-mono text-xs break-all text-muted-foreground">
                        {log.message_id || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
